import React, { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { useCourseProgress } from '../hooks/useCourseProgress';
import { FUNDAMENTALS_COURSE, findLevelById, getAllLevelIds, FINAL_QUIZ_ID } from '../config/courseContent';
import { CourseOverview } from '../components/course/CourseOverview';
import { LevelView } from '../components/course/LevelView';
import { FinalQuizView } from '../components/course/FinalQuizView';
import { AuthGateOverlay } from '../components/course/AuthGateOverlay';

type View = { type: 'overview' } | { type: 'level'; levelId: string } | { type: 'final-quiz' };

export const CoursePage: React.FC = () => {
  const { user } = useAuth();
  const { completedLevels, isLevelUnlocked, completeLevel, isLoading } = useCourseProgress();
  const [view, setView] = useState<View>({ type: 'overview' });
  const [showAuthGate, setShowAuthGate] = useState(false);

  const allLevelIds = getAllLevelIds(FUNDAMENTALS_COURSE);

  const handleSelectLevel = useCallback(
    (levelId: string) => {
      // Guest can only access level 1
      if (!user && levelId !== allLevelIds[0]) {
        setShowAuthGate(true);
        return;
      }
      setView({ type: 'level', levelId });
    },
    [user, allLevelIds]
  );

  const handleSelectFinalQuiz = useCallback(() => {
    if (!user) {
      setShowAuthGate(true);
      return;
    }
    setView({ type: 'final-quiz' });
  }, [user]);

  const handleLockedClick = useCallback(
    (needsAuth: boolean) => {
      if (needsAuth || !user) {
        setShowAuthGate(true);
      }
      // If signed in and locked, the overview button already shows as locked visually
    },
    [user]
  );

  const handleBack = useCallback(() => {
    setView({ type: 'overview' });
  }, []);

  const handleLevelComplete = useCallback(
    (levelId: string, sectionId: string, quizScore: number, quizTotal: number) => {
      completeLevel(levelId, sectionId, quizScore, quizTotal);
    },
    [completeLevel]
  );

  const handleFinalQuizComplete = useCallback(
    (score: number, total: number) => {
      completeLevel(FINAL_QUIZ_ID, 'final', score, total);
    },
    [completeLevel]
  );

  const handleNextLevel = useCallback(() => {
    if (view.type !== 'level') {
      setView({ type: 'overview' });
      return;
    }

    const currentIdx = allLevelIds.indexOf(view.levelId);
    if (currentIdx < allLevelIds.length - 1) {
      const nextId = allLevelIds[currentIdx + 1];
      // Guest can only access level 1
      if (!user && nextId !== allLevelIds[0]) {
        setShowAuthGate(true);
        return;
      }
      setView({ type: 'level', levelId: nextId });
    } else {
      // After last level, go to final quiz or overview
      if (isLevelUnlocked(FINAL_QUIZ_ID)) {
        setView({ type: 'final-quiz' });
      } else {
        setView({ type: 'overview' });
      }
    }
  }, [view, allLevelIds, user, isLevelUnlocked]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-6 h-6 border-2 border-[var(--clue-green-accent)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Wallstreetle | Learn Fundamental Analysis</title>
        <meta
          name="description"
          content="Free course teaching the basics of fundamental stock analysis — financial statements, key metrics, and how to evaluate companies."
        />
      </Helmet>

      <div className="min-h-[60vh]">
        {view.type === 'overview' && (
          <CourseOverview
            course={FUNDAMENTALS_COURSE}
            completedLevels={completedLevels}
            isLevelUnlocked={isLevelUnlocked}
            onSelectLevel={handleSelectLevel}
            onSelectFinalQuiz={handleSelectFinalQuiz}
            onLockedClick={handleLockedClick}
          />
        )}

        {view.type === 'level' && (() => {
          const result = findLevelById(FUNDAMENTALS_COURSE, view.levelId);
          if (!result) return null;
          const { section, level } = result;
          const currentIdx = allLevelIds.indexOf(view.levelId);
          const hasNext = currentIdx < allLevelIds.length - 1 || isLevelUnlocked(FINAL_QUIZ_ID);

          return (
            <div className="max-w-md mx-auto px-4 py-4 h-[calc(100vh-8rem)]">
              <LevelView
                key={level.id}
                level={level}
                sectionId={section.id}
                hasNextLevel={hasNext}
                onComplete={handleLevelComplete}
                onBack={handleBack}
                onNext={handleNextLevel}
              />
            </div>
          );
        })()}

        {view.type === 'final-quiz' && (
          <div className="max-w-md mx-auto px-4 py-4 h-[calc(100vh-8rem)]">
            <FinalQuizView
              questions={FUNDAMENTALS_COURSE.finalQuiz}
              onComplete={handleFinalQuizComplete}
              onBack={handleBack}
            />
          </div>
        )}
      </div>

      {/* Auth gate overlay */}
      {showAuthGate && <AuthGateOverlay onDismiss={() => setShowAuthGate(false)} />}
    </>
  );
};
